import { Option, Schema } from "effect";
import { schemaParser } from "./schema-compat.ts";

/**
 * Pure timeline and playback math for the flow-recording players.
 *
 * Everything in this module is DOM-free and side-effect-free so the frame
 * scheduling, rrweb stream selection, skip-inactive compression, marker
 * positioning, and lane highlighting logic stay unit-testable under
 * `@effect/vitest` (test/effect/flow-recording-player-model.spec.ts). The React
 * players in `FlowRecordingsExplorer.tsx` consume these functions but add no
 * timeline math of their own.
 */

// ---------------------------------------------------------------------------
// rrweb stream selection
// ---------------------------------------------------------------------------

/** The event shape required by rrweb's replay engine. */
export const RrwebReplayEvent = /* @__PURE__ */ (() => Schema.StructWithRest(Schema.Struct({
  timestamp: Schema.Finite.pipe(Schema.mutableKey),
  type: Schema.Number.check(Schema.makeFilter(Number.isInteger)).pipe(Schema.mutableKey),
  data: Schema.Unknown.pipe(Schema.optional, Schema.mutableKey),
}), [Schema.Record(Schema.String, Schema.mutableKey(Schema.Unknown))]))();
export type RrwebReplayEvent = typeof RrwebReplayEvent.Type;

const RrwebReplayStream = /* @__PURE__ */ (() => Schema.Struct({
  events: Schema.Array(Schema.Struct({
    event: RrwebReplayEvent.pipe(Schema.mutableKey),
    order: Schema.Number.pipe(Schema.mutableKey),
  })).pipe(Schema.mutable, Schema.mutableKey),
  viewportArea: Schema.Number.pipe(Schema.mutableKey),
  hasFullSnapshot: Schema.Boolean.pipe(Schema.mutableKey),
}))();
type RrwebReplayStream = typeof RrwebReplayStream.Type;

const decodeReplayEvent = /* @__PURE__ */ (() => Schema.decodeUnknownOption(RrwebReplayEvent))();
const replayWindowId = /* @__PURE__ */ (() => Schema.decodeUnknownOption(Schema.String))();
const asRrwebReplayEvent = (value: unknown): RrwebReplayEvent | null =>
  Option.getOrNull(decodeReplayEvent(value));
const PositiveDimension = /* @__PURE__ */ (() => Schema.Finite.check(Schema.isGreaterThan(0)))();
const Viewport = /* @__PURE__ */ (() => Schema.Struct({ width: PositiveDimension, height: PositiveDimension }))();
const viewport = /* @__PURE__ */ (() => Schema.decodeUnknownOption(Viewport))();
function rrwebMetaViewportArea(event: RrwebReplayEvent): number {
  return event.type === 4 ? Option.match(viewport(event.data), {
    onSome: ({ width, height }) => width * height,
    onNone: () => 0
  }) : 0;
}

/**
 * Select the top-level rrweb document streams from captured `[windowId,
 * event]` entries. Playwright init scripts also run in child frames, and
 * flattening those independent snapshots into one replay lets a child frame's
 * document replace the top-level page. Top-level documents have the largest
 * non-zero Meta-event viewport; equal-size streams are retained so navigation
 * across multiple top-level documents still replays. Legacy streams without
 * usable viewport metadata fall back to the snapshot stream with most events.
 */
export function selectRrwebReplayEvents(
  entries: readonly (readonly [unknown, unknown])[]
): RrwebReplayEvent[] {
  const streams = new Map<string, RrwebReplayStream>();
  entries.forEach(([rawWindowId, rawEvent], order) => {
    const windowId = Option.getOrNull(replayWindowId(rawWindowId));
    if (windowId === null) return;
    const event = asRrwebReplayEvent(rawEvent);
    if (!event) return;
    let stream = streams.get(windowId);
    if (!stream) {
      stream = { events: [], viewportArea: 0, hasFullSnapshot: false };
      streams.set(windowId, stream);
    }
    stream.events.push({ event, order });
    stream.viewportArea = Math.max(
      stream.viewportArea,
      rrwebMetaViewportArea(event)
    );
    if (event.type === 2) stream.hasFullSnapshot = true;
  });

  const allStreams = [...streams.values()];
  const snapshotStreams = allStreams.filter((stream) => stream.hasFullSnapshot);
  const candidates = snapshotStreams.length > 0 ? snapshotStreams : allStreams;
  if (candidates.length === 0) return [];

  const maxViewportArea = Math.max(
    ...candidates.map((stream) => stream.viewportArea)
  );
  let selected: RrwebReplayStream[];
  if (maxViewportArea > 0) {
    selected = candidates.filter(
      (stream) => stream.viewportArea === maxViewportArea
    );
  } else {
    const maxEventCount = Math.max(
      ...candidates.map((stream) => stream.events.length)
    );
    selected = candidates.filter(
      (stream) => stream.events.length === maxEventCount
    );
  }

  return selected
    .flatMap((stream) => stream.events)
    .sort(
      (a, b) => a.event.timestamp - b.event.timestamp || a.order - b.order
    )
    .map(({ event }) => event);
}

// ---------------------------------------------------------------------------
// Frame timeline
// ---------------------------------------------------------------------------

/** One playable frame, resolved to an absolute ranged read. */
export const FlowRecordingFrameRef = /* @__PURE__ */ (() => Schema.Struct({
  /** Absolute capture timestamp on the host wall clock (ms). */
  timestampMs: Schema.Number.pipe(Schema.mutableKey),
  /** Sequence of the video segment holding the frame's JPEG bytes. */
  segmentSeq: Schema.Number.pipe(Schema.mutableKey),
  /** Offset of the raw JPEG bytes within that segment, for ranged reads. */
  byteOffset: Schema.Number.pipe(Schema.mutableKey),
  /** JPEG byte length. */
  byteLength: Schema.Number.pipe(Schema.mutableKey),
}))();
export type FlowRecordingFrameRef = typeof FlowRecordingFrameRef.Type;

/**
 * The slice of a manifest segment the timeline builder needs. Matches
 * `FlowRecordingSegmentSummary` structurally; `frameIndex` arrives untyped
 * from the manifest JSON and is validated entry by entry.
 */
export const FlowRecordingTimelineSegment = /* @__PURE__ */ (() => Schema.Struct({
  seq: Schema.Number.pipe(Schema.mutableKey),
  kind: Schema.String.pipe(Schema.mutableKey),
  frameIndex: Schema.Unknown.pipe(Schema.optional, Schema.mutableKey),
}))();
export type FlowRecordingTimelineSegment = typeof FlowRecordingTimelineSegment.Type;

const FrameIndexEntry = /* @__PURE__ */ (() => Schema.Struct({
  timestampMs: Schema.Finite,
  byteOffset: Schema.Number.check(Schema.makeFilter(Number.isInteger), Schema.isGreaterThanOrEqualTo(0)),
  byteLength: Schema.Number.check(Schema.makeFilter(Number.isInteger), Schema.isGreaterThan(0))
}))();
const isFrameIndexEntry = /* @__PURE__ */ (() => Schema.is(FrameIndexEntry))();
// Validate the envelope without traversing entries twice or invoking an
// accessor before the existing per-entry decoder reaches it.
const isFrameIndex = /* @__PURE__ */ (() => Schema.is(Schema.declare((value): value is readonly unknown[] => Array.isArray(value))))();
const FrameByteRange = /* @__PURE__ */ (() => Schema.Struct({
  byteOffset: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  byteLength: Schema.Int.check(Schema.isGreaterThan(0)),
  end: Schema.Int
}))();

/**
 * Flatten every video segment's per-frame offset index into one timeline of
 * absolute `{ timestampMs, segmentSeq, byteOffset, byteLength }` entries
 * sorted by capture time. Non-video segments, missing indexes, and malformed
 * index entries are skipped rather than corrupting the timeline.
 */
export function buildFrameTimeline(
  segments: readonly FlowRecordingTimelineSegment[]
): FlowRecordingFrameRef[] {
  const timeline: FlowRecordingFrameRef[] = [];
  for (const segment of segments) {
    if (segment.kind !== "video") continue;
    if (!isFrameIndex(segment.frameIndex)) continue;
    for (const entry of segment.frameIndex) {
      if (!isFrameIndexEntry(entry)) continue;
      timeline.push({
        timestampMs: entry.timestampMs,
        segmentSeq: segment.seq,
        byteOffset: entry.byteOffset,
        byteLength: entry.byteLength
      });
    }
  }
  timeline.sort(
    (a, b) =>
      a.timestampMs - b.timestampMs ||
      a.segmentSeq - b.segmentSeq ||
      a.byteOffset - b.byteOffset
  );
  return timeline;
}

/**
 * Binary-search the frame to display at time `tMs`: the greatest index whose
 * timestamp is <= `tMs`. Returns -1 for an empty timeline, a non-finite
 * time, or a time before the first frame.
 */
export function frameAtTime(
  frameTimeline: readonly FlowRecordingFrameRef[],
  tMs: number
): number {
  if (frameTimeline.length === 0 || !Number.isFinite(tMs)) return -1;
  if (tMs < frameTimeline[0]!.timestampMs) return -1;
  let low = 0;
  let high = frameTimeline.length - 1;
  while (low < high) {
    const mid = (low + high + 1) >> 1;
    if (frameTimeline[mid]!.timestampMs <= tMs) low = mid;
    else high = mid - 1;
  }
  return low;
}

/**
 * Resolve one indexed JPEG as a zero-copy view over its downloaded fpack
 * segment. The player fetches whole segments so adjacent animation frames do
 * not each pay for a separate HTTP range request.
 */
export function frameBytesFromSegment(
  segment: ArrayBuffer,
  frame: Pick<FlowRecordingFrameRef, "byteOffset" | "byteLength">
): Uint8Array<ArrayBuffer> {
  const { byteOffset, byteLength } = frame;
  const end = byteOffset + byteLength;
  schemaParser(FrameByteRange.check(Schema.makeFilter(range => !(range.end > segment.byteLength))),
    () => new RangeError("Video frame range falls outside its fpack segment.")
  )({ byteOffset, byteLength, end });
  return new Uint8Array(segment, byteOffset, byteLength);
}

// ---------------------------------------------------------------------------
// Marker pip positioning
// ---------------------------------------------------------------------------

/**
 * Position of a marker pip on the timeline strip as a fraction of the
 * recording duration, clamped to [0, 1]. A degenerate (zero or negative
 * duration) timeline positions everything at 0.
 */
export function markerPipFraction(
  timestampMs: number,
  startMs: number,
  endMs: number
): number {
  if (
    !Number.isFinite(timestampMs) ||
    !Number.isFinite(startMs) ||
    !Number.isFinite(endMs) ||
    endMs <= startMs
  ) {
    return 0;
  }
  const fraction = (timestampMs - startMs) / (endMs - startMs);
  return Math.min(1, Math.max(0, fraction));
}

// ---------------------------------------------------------------------------
// Skip-inactive gap compression
// ---------------------------------------------------------------------------

/** Recording gaps longer than this are considered inactive. */
export const SKIP_INACTIVE_GAP_THRESHOLD_MS = 5_000;
/** Each inactive gap plays back compressed to this long. */
export const SKIP_INACTIVE_COMPRESSED_GAP_MS = 500;

/** One piecewise-linear span of the playback-time <-> recording-time map. */
export const PlaybackTimeSpan = /* @__PURE__ */ (() => Schema.Struct({
  recordingStartMs: Schema.Number.pipe(Schema.mutableKey),
  recordingEndMs: Schema.Number.pipe(Schema.mutableKey),
  playbackStartMs: Schema.Number.pipe(Schema.mutableKey),
  playbackEndMs: Schema.Number.pipe(Schema.mutableKey),
  /** True when this span is an inactive gap squeezed to the compressed length. */
  compressed: Schema.Boolean.pipe(Schema.mutableKey),
}))();
export type PlaybackTimeSpan = typeof PlaybackTimeSpan.Type;

/**
 * A bidirectional mapping between recording time (absolute wall-clock ms)
 * and playback time (ms since playback start) with inactive gaps compressed.
 */
export const PlaybackTimeMap = /* @__PURE__ */ (() => Schema.Struct({
  spans: Schema.Array(PlaybackTimeSpan).pipe(Schema.mutable, Schema.mutableKey),
  recordingStartMs: Schema.Number.pipe(Schema.mutableKey),
  recordingEndMs: Schema.Number.pipe(Schema.mutableKey),
  recordingDurationMs: Schema.Number.pipe(Schema.mutableKey),
  playbackDurationMs: Schema.Number.pipe(Schema.mutableKey),
}))();
export type PlaybackTimeMap = typeof PlaybackTimeMap.Type;

/**
 * Build the skip-inactive map from the recording's activity timestamps
 * (frame captures, rrweb events, markers — anything that proves the page was
 * doing something). Gaps between consecutive activity points longer than
 * `gapThresholdMs` compress to `compressedGapMs` of playback; everything
 * else plays at recorded duration.
 */
export function buildPlaybackTimeMap(
  activityTimestamps: readonly number[],
  recordingStartMs: number,
  recordingEndMs: number,
  options: { gapThresholdMs?: number; compressedGapMs?: number } = {}
): PlaybackTimeMap {
  const gapThresholdMs = options.gapThresholdMs ?? SKIP_INACTIVE_GAP_THRESHOLD_MS;
  const compressedGapMs =
    options.compressedGapMs ?? SKIP_INACTIVE_COMPRESSED_GAP_MS;
  const endMs = Math.max(recordingStartMs, recordingEndMs);
  const points = Array.from(
    new Set(
      activityTimestamps.filter(
        (value) =>
          Number.isFinite(value) && value > recordingStartMs && value < endMs
      )
    )
  ).sort((a, b) => a - b);
  const boundaries = [recordingStartMs, ...points, endMs];

  const spans: PlaybackTimeSpan[] = [];
  let playbackCursor = 0;
  let identityStart = recordingStartMs;
  const pushIdentity = (upToMs: number) => {
    if (upToMs <= identityStart) return;
    spans.push({
      recordingStartMs: identityStart,
      recordingEndMs: upToMs,
      playbackStartMs: playbackCursor,
      playbackEndMs: playbackCursor + (upToMs - identityStart),
      compressed: false
    });
    playbackCursor += upToMs - identityStart;
  };
  for (let index = 0; index < boundaries.length - 1; index += 1) {
    const gapStart = boundaries[index]!;
    const gapEnd = boundaries[index + 1]!;
    const gap = gapEnd - gapStart;
    if (gap <= gapThresholdMs) continue;
    pushIdentity(gapStart);
    const playbackLength = Math.min(compressedGapMs, gap);
    spans.push({
      recordingStartMs: gapStart,
      recordingEndMs: gapEnd,
      playbackStartMs: playbackCursor,
      playbackEndMs: playbackCursor + playbackLength,
      compressed: true
    });
    playbackCursor += playbackLength;
    identityStart = gapEnd;
  }
  pushIdentity(endMs);

  return {
    spans,
    recordingStartMs,
    recordingEndMs: endMs,
    recordingDurationMs: endMs - recordingStartMs,
    playbackDurationMs: playbackCursor
  };
}

/** Map an absolute recording time onto the compressed playback clock. */
export function recordingToPlaybackTime(
  map: PlaybackTimeMap,
  recordingMs: number
): number {
  if (map.spans.length === 0) return 0;
  if (recordingMs <= map.recordingStartMs) return 0;
  if (recordingMs >= map.recordingEndMs) return map.playbackDurationMs;
  for (const span of map.spans) {
    if (recordingMs > span.recordingEndMs) continue;
    const recordingLength = span.recordingEndMs - span.recordingStartMs;
    if (recordingLength <= 0) return span.playbackStartMs;
    const fraction = (recordingMs - span.recordingStartMs) / recordingLength;
    return (
      span.playbackStartMs +
      fraction * (span.playbackEndMs - span.playbackStartMs)
    );
  }
  return map.playbackDurationMs;
}

/** Map a compressed playback clock reading back to absolute recording time. */
export function playbackToRecordingTime(
  map: PlaybackTimeMap,
  playbackMs: number
): number {
  if (map.spans.length === 0) return map.recordingStartMs;
  if (playbackMs <= 0) return map.recordingStartMs;
  if (playbackMs >= map.playbackDurationMs) return map.recordingEndMs;
  for (const span of map.spans) {
    if (playbackMs > span.playbackEndMs) continue;
    const playbackLength = span.playbackEndMs - span.playbackStartMs;
    if (playbackLength <= 0) return span.recordingStartMs;
    const fraction = (playbackMs - span.playbackStartMs) / playbackLength;
    return (
      span.recordingStartMs +
      fraction * (span.recordingEndMs - span.recordingStartMs)
    );
  }
  return map.recordingEndMs;
}

// ---------------------------------------------------------------------------
// Playback clock stepping
// ---------------------------------------------------------------------------

/**
 * Advance the playhead by `elapsedRealMs` of wall time at `speed`. Without a
 * map the recording clock advances linearly; with a skip-inactive map the
 * step happens on the compressed playback clock so inactive gaps fly by at
 * their compressed length. The result is clamped to the map's recording
 * window when a map is provided.
 */
export function stepPlaybackClock(
  positionRecordingMs: number,
  elapsedRealMs: number,
  speed: number,
  map?: PlaybackTimeMap
): number {
  const rate = Number.isFinite(speed) && speed > 0 ? speed : 1;
  const advance = Math.max(0, elapsedRealMs) * rate;
  if (!map) return positionRecordingMs + advance;
  const playback = recordingToPlaybackTime(map, positionRecordingMs) + advance;
  return playbackToRecordingTime(
    map,
    Math.min(playback, map.playbackDurationMs)
  );
}

// ---------------------------------------------------------------------------
// Context-lane highlighting
// ---------------------------------------------------------------------------

/** Lane rows highlight while the playhead is within this trailing window. */
export const LANE_HIGHLIGHT_WINDOW_MS = 1_500;

/**
 * True when a console/network lane row should highlight for the current
 * playhead: the row's timestamp lies within `[playheadMs - windowMs,
 * playheadMs]` — recently crossed, never upcoming.
 */
export function isLaneRowHighlighted(
  rowTimestampMs: number,
  playheadMs: number,
  windowMs = LANE_HIGHLIGHT_WINDOW_MS
): boolean {
  if (!Number.isFinite(rowTimestampMs) || !Number.isFinite(playheadMs)) {
    return false;
  }
  return (
    rowTimestampMs <= playheadMs && rowTimestampMs >= playheadMs - windowMs
  );
}

// ---------------------------------------------------------------------------
// Readout formatting
// ---------------------------------------------------------------------------

/** Format a recording-relative offset as `m:ss.t` for player readouts. */
export function formatPlayerClock(relativeMs: number): string {
  const clamped = Number.isFinite(relativeMs) ? Math.max(0, relativeMs) : 0;
  const totalSeconds = Math.floor(clamped / 1_000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const tenths = Math.floor((clamped % 1_000) / 100);
  return `${minutes}:${String(seconds).padStart(2, "0")}.${tenths}`;
}
